import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ChangeEvent,
  VirtualScrollComponent,
} from 'angular2-virtual-scroll/dist/virtual-scroll';
import { List, Map } from 'immutable';
import { Observable, Subject } from 'rxjs';
import { AppStore } from '../../store/app.store';
import { DialogService } from '../dialog/dialog.service';
import { BannerCanvasTypesEnum } from '../features-data/features-data.data';
import { FeaturesDataStore } from '../features-data/features-data.store';
import { IIcon } from '../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../shared/components/icon/icons-sizes.enum';
import { FillToBottomActionsEnum } from '../shared/directives/fill-to-bottom/fill-to-bottom.directive';
import {
  IWaypoint,
  WaypointActionTriggersEnum,
  WaypointDirectionsEnum,
} from '../shared/directives/waypoints/waypoints.directive';
import { IMetadata, INoItemsOptions } from './feed.interface';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-feed',
  templateUrl: 'feed.component.html',
  styleUrls: ['feed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() feed: List<any>;
  @Input() noItemsOptions: INoItemsOptions;
  @Input() groupByFn: Function;
  @Input() sortGroupsByFn: Function;
  @Input() sortGroupsComparator: Function;
  @Input() groupMetadata: Map<string, IMetadata>;
  @Input() trackByFn: Function;
  @Input() fetching: boolean;
  @Input() lastPageFetched: boolean;
  @Input() displayEmptyGroups: boolean;
  @Input() enablePagination: boolean;
  @Input() enableVirtualScroll: boolean;
  @Input() virtualScrollBufferAmount: number = 20;

  @Output() onInfinitePagination: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  onNoItemsPrimaryActionClick: EventEmitter<void> = new EventEmitter<void>();

  @ContentChild(TemplateRef, { static: false }) template: TemplateRef<any>;

  @ViewChild('feedContainer', { static: false }) feedContainer: ElementRef;
  @ViewChild(VirtualScrollComponent, { static: false })
  virtualScrollCmpt: VirtualScrollComponent;

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _fillToBottomActionTriggers: Subject<FillToBottomActionsEnum> = new Subject<FillToBottomActionsEnum>();
  private _waypointActionTriggers: Subject<WaypointActionTriggersEnum> = new Subject<WaypointActionTriggersEnum>();

  feedItems: any;
  noItemsIcon: IIcon;
  showFeatureHighlight: boolean;
  premiumIconSize: IconSizesEnum;
  buffer: any[];
  loadingSpinnerIcon: IIcon;
  grouped: boolean;
  fillToBottomActionTriggers$: Observable<FillToBottomActionsEnum>;
  waypoints: List<IWaypoint>;
  waypointActionTriggers$: Observable<WaypointActionTriggersEnum> = this._waypointActionTriggers.asObservable();

  constructor(
    private _featuresDataStore: FeaturesDataStore,
    private _appStore: AppStore,
    private _dialogService: DialogService
  ) {}

  ngOnChanges(changes) {
    const noItemsOptionsChanges = changes.noItemsOptions;
    const feedChanges = changes.feed;
    const groupMetadataChanges = changes.groupMetadata;

    if (noItemsOptionsChanges && this.noItemsOptions) {
      if (this.noItemsOptions.iconName) {
        this.noItemsIcon = {
          name: this.noItemsOptions.iconName,
          size: IconSizesEnum.huge,
        };
      } else {
        this.noItemsIcon = null;
      }

      const featureName = this.noItemsOptions.featureName;

      if (featureName) {
        this._featuresDataStore
          .hasFeatureHighlightCanvas(featureName)
          .pipe(takeUntil(this._destroyed))
          .subscribe((hasFeatureHighlight: boolean) => {
            this.showFeatureHighlight = hasFeatureHighlight;
          });
      } else {
        this.showFeatureHighlight = false;
      }
    }

    if (feedChanges || groupMetadataChanges) {
      if (this.feed) {
        if (!this.enableVirtualScroll && this.feedContainer) {
          this.waypoints = List([
            {
              element: this.feedContainer.nativeElement,
              handler: (direction: WaypointDirectionsEnum) => {
                if (direction === WaypointDirectionsEnum.down) {
                  this.fetchMore();
                }
              },
              offset: 100,
              fromBottom: true,
              context: this.feedContainer.nativeElement.closest(
                '.scroll-container'
              ),
            },
          ]);
        }

        this.feedItems = this.orderFeed(this.feed);
      } else {
        this.feedItems = null;
      }

      if (this.enableVirtualScroll) {
        this.buffer = this.feedItems && this.feedItems.toArray();

        if (this.feedItems) {
          setTimeout(
            () =>
              this._fillToBottomActionTriggers.next(
                FillToBottomActionsEnum.resize
              ),
            50
          );
        }
      } else {
        setTimeout(
          () =>
            this._waypointActionTriggers.next(
              WaypointActionTriggersEnum.refresh
            ),
          50
        );
      }
    }

    this.grouped = !!this.groupByFn;
  }

  ngOnInit() {
    this.premiumIconSize = IconSizesEnum.large;
    this.loadingSpinnerIcon = {
      name: 'loading-spinner',
      size: IconSizesEnum.medium,
    };
    this.fillToBottomActionTriggers$ = this._fillToBottomActionTriggers.asObservable();
  }

  ngAfterViewInit() {
    this._appStore.globalBanner$
      .pipe(takeUntil(this._destroyed), debounceTime(100))
      .subscribe(() =>
        this._fillToBottomActionTriggers.next(FillToBottomActionsEnum.resize)
      );
  }

  orderFeed(feedItems: List<any>) {
    let tmpFeedItems = feedItems as any;

    if (!!this.groupByFn) {
      tmpFeedItems = tmpFeedItems.groupBy(this.groupByFn);

      if (this.displayEmptyGroups && this.groupMetadata) {
        const emptyGroups = this.groupMetadata.reduce(
          (result: Map<string, List<any>>, groupMetadata: any, key: any): any =>
            result.set(key, List([])),
          Map({})
        );

        tmpFeedItems = emptyGroups.merge(tmpFeedItems);
      }

      if (!!this.sortGroupsByFn) {
        tmpFeedItems = tmpFeedItems.sortBy(
          this.sortGroupsByFn,
          this.sortGroupsComparator
        );
      }

      if (this.enableVirtualScroll) {
        let itemPos = 1;

        tmpFeedItems = tmpFeedItems.reduce(
          (result: List<any>, groupedFeedItems: any, key: any): any => {
            const metadata: IMetadata = (this.groupMetadata &&
              this.groupMetadata.get(key)) || {
              label: key,
              data: '',
            };
            const groupedItemsSize = groupedFeedItems.size || 0;

            return result
              .push({
                sectionHeader: {
                  label: metadata.label,
                  meta: metadata.data,
                },
              })
              .concat(
                groupedFeedItems.map((feedItem, index) => ({
                  position: itemPos++,
                  value: feedItem,
                  lastGroupedItem: index + 1 === groupedItemsSize,
                }))
              ) as List<any>;
          },
          List([])
        ) as List<any>;
      }
    }

    return tmpFeedItems;
  }

  fetchMore(event?: ChangeEvent) {
    if (event && event.end !== this.buffer.length) {
      return;
    }

    if (this.enablePagination && !this.fetching) {
      this.fetching = true;

      this.onInfinitePagination.emit();
    }
  }

  showFeatureHighlightDialog() {
    if (this.noItemsOptions) {
      this._dialogService.openGrowthDialog(
        BannerCanvasTypesEnum.feature_highlight,
        this.noItemsOptions.featureName
      );
    }
  }

  refreshVirtualScroll() {
    this.virtualScrollCmpt && this.virtualScrollCmpt.refresh(true);
  }

  ngOnDestroy() {
    if (this.noItemsOptions) {
      this._dialogService.closeGrowthDialog(
        BannerCanvasTypesEnum.feature_highlight,
        this.noItemsOptions.featureName
      );
    }

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
